import { IconAlert } from '@blockchain-com/icons'
import I18n from 'components/I18n'
import Icon from 'components/Icons/Icon'
import { OverlayLoader } from 'components/Loaders'
import Money from 'components/Money'
import Tooltips from 'components/Tooltips'
import { actions, selectors } from 'data'
import { WithdrawalRequest } from 'data/types'
import messages from 'lang'
import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Header, ModalCard, ModalTitle } from 'scenes/Cashflow/styles'
import styled from 'styled-components'

import CurrencyAmountInput from '../CurrencyAmountInput'
import Fee from '../Fee'
import { Button, InfoPill } from '../styles'
import Balance from './Balance'
import Body from './Body'
import { getCryptoAmountError } from './getAmountError'
import Success from './Success'
import UpgradePrompt from './UpgradePrompt'

const Footer = styled.footer`
  margin: 0.5rem 0 0 0;
`

const ConvertedValue = styled.span`
  svg {
    vertical-align: text-top;
    margin-left: 0.25rem;
  }
`

const StyledHeader = styled(Header)`
  margin-bottom: 0.375rem;
`

const getBeneficiaryError = (value, metadata) => {
  if (/\s/.test(metadata) || /\s/.test(value)) {
    return <I18n data={messages.validation_has_no_spaces} />
  }
  return null
}

const WithdrawCryptoFormModal = ({ currency }) => {
  const localCurrency = useSelector(selectors.auth.getLocalCurrency)
  const twoFactorStatus = useSelector(selectors.auth.getTwoFactorStatus)
  const isSilver = useSelector(selectors.auth.isUserSilverishTier)
  const wsBalance = useSelector(selectors.balance.getAll).find(
    (balance) => balance.currency === currency
  )
  const info = useSelector(selectors.payments.withdraw.getWithdrawInfo)
  const limits = useSelector(selectors.auth.getCryptoLimits)
  const isSubmitting = useSelector(selectors.withdrawal.isSubmitting)
  const withdrawal = useSelector(selectors.withdrawal.getSubmittedOrder)

  const [beneficiaryValue, setBeneficiaryValue] = useState<string>('')
  const [beneficiaryType, setBeneficiaryType] = useState<string>('')
  const [beneficiaryMetadata, setBeneficiaryMetadata] = useState<string>('')
  const [secondFactor, setSecondFactor] = useState<string>('')
  const [amount, setAmount] = useState<string>('')

  const dispatch = useDispatch()
  const has2FASetup = twoFactorStatus && twoFactorStatus.length > 0

  const handleSubmit = (e) => {
    e.preventDefault()
    if (twoFactorStatus && twoFactorStatus.length === 0) return

    const payload: WithdrawalRequest = {
      amount: parseFloat(amount),
      beneficiary: {
        metadata: beneficiaryMetadata,
        type: beneficiaryType,
        value: beneficiaryValue
      },
      currency,
      input_amount: amount,
      input_currency: currency,
      isFiat: false,
      secondFactor
    }

    dispatch(actions.withdrawal.submit(payload))
    dispatch(
      actions.analytics.trackEvent({
        key: 'Withdrawal Amount Entered',
        properties: {
          amount: payload.amount,
          currency: payload.currency,
          input_amount: payload.input_amount,
          input_currency: payload.input_currency,
          withdrawal_method: 'CRYPTO' // FIXME, could be STABLECOIN
        }
      })
    )
  }

  const handleClose = () => dispatch(actions.app.hideModal('WITHDRAWAL'))

  const handleUpgrade = () => {
    dispatch(
      actions.analytics.trackEvent({
        key: 'Upgrade Verification Clicked',
        properties: {
          origin: 'WITHDRAW',
          tier: 2
        }
      })
    )
    dispatch(actions.app.showModal('TIER_TWO'))
  }

  useEffect(() => {
    dispatch(actions.payments.withdraw.fetch(currency))
  }, [currency])

  useEffect(() => {
    dispatch(actions.twoFactor.getStatus())

    return () => {
      dispatch(actions.withdrawal.reset())
    }
  }, [])

  if (withdrawal) return <Success amount={amount} currency={currency} onBack={handleClose} />

  const isLoading = isSubmitting || !info?.DEFAULT

  if (isLoading) {
    return (
      <ModalCard>
        <StyledHeader>
          <ModalTitle>
            <I18n data={messages.withdrawl_currency} currency={currency} />
          </ModalTitle>
        </StyledHeader>
        <OverlayLoader />
      </ModalCard>
    )
  }

  const paymentMethodAndCurrencyInfo = info.DEFAULT
  const { fee } = paymentMethodAndCurrencyInfo
  const minAmount = paymentMethodAndCurrencyInfo.min
  const maxAmount = paymentMethodAndCurrencyInfo.max
  const userTierMax = limits.limit - limits.used
  const rate = wsBalance ? wsBalance.rate : 1
  const convertedValue = Number(amount) * rate
  const beneficiaryError = getBeneficiaryError(beneficiaryValue, beneficiaryMetadata)
  const amountError = getCryptoAmountError({
    amount,
    cryptoCurrency: currency,
    maxAmount,
    minAmount
  })

  return (
    <ModalCard>
      <StyledHeader>
        <ModalTitle>
          <I18n data={messages.withdrawl_currency} currency={currency} />
        </ModalTitle>
      </StyledHeader>
      <form autoComplete='off' onSubmit={handleSubmit}>
        <Balance
          mainAmount={maxAmount}
          mainCurrency={currency}
          secondaryAmount={maxAmount * rate}
          secondaryCurrency={localCurrency}
          setMaximumAmount={() => setAmount(maxAmount.toString())}
        />
        <CurrencyAmountInput
          amount={amount}
          currency={currency}
          e2e='withdrawAmount'
          onAmountChange={setAmount}
        />

        {amountError ? (
          <InfoPill $isError>
            <span data-e2e='withdrawAmountError'>{amountError}</span>
          </InfoPill>
        ) : rate ? (
          <InfoPill>
            <ConvertedValue>
              ~ <Money amount={convertedValue} currency={localCurrency} />{' '}
              <Tooltips name='estimated.amount' placement='right' wrap={false}>
                <Icon color='grey400' size='sm'>
                  <IconAlert />
                </Icon>
              </Tooltips>
            </ConvertedValue>
          </InfoPill>
        ) : null}

        <Body
          availableFunds={maxAmount > 0}
          beneficiaryError={beneficiaryError}
          beneficiaryMetadata={beneficiaryMetadata}
          beneficiaryType={beneficiaryType}
          beneficiaryValue={beneficiaryValue}
          currency={currency}
          onBeneficiaryMetadataChange={setBeneficiaryMetadata}
          onBeneficiaryTypeChange={setBeneficiaryType}
          onBeneficiaryValueChange={setBeneficiaryValue}
          onSecondFactorChange={setSecondFactor}
          secondFactor={secondFactor}
        />

        {fee > 0 && <Fee amount={fee} currency={currency} withdrawal={amount} />}
        {isSilver && Number(amount) >= userTierMax ? (
          <UpgradePrompt onUpgrade={handleUpgrade} />
        ) : null}

        <Footer>
          <Button
            disabled={!amount || !!amountError || !!beneficiaryError || !has2FASetup}
            onClick={handleSubmit}
            type='submit'
          >
            <I18n data={messages.withdraw_title} />
          </Button>
        </Footer>
      </form>
    </ModalCard>
  )
}

export default WithdrawCryptoFormModal
